/**
 * useCertificationMembersV2
 *
 * Reads cert.cert_household.cert_members from Redux and maps CertMember[]
 * to CertHouseholdMemberData[]. With the new v2 endpoint, the Redux slice
 * already holds the correct shape — no adaptation needed beyond field mapping.
 */

import { useMemo, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import type { Certification, CertMember } from 'src/types/Certification';
import type { CertificationMemberView } from 'src/types/v2/CertificationMember';
import axios from 'src/utils/axios';

export interface OtherCertMembershipData {
  certification_id: number;
  certification_name: string;
  category: string;
  status: string;
  is_terminal: boolean;
  member_type_id: number;
  member_type_name: string;
  cert_member_id: number;
}

export interface CertHouseholdMemberData {
  cert_member_id: number;
  user_household_member_id: number;
  household_role: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  preferred_language: string;
  member_type_id: number;
  member_type_name: string;
  other_cert_memberships: OtherCertMembershipData[];
}

export interface ActiveCertification {
  certification_id: number;
  certification_name: string;
  category: string;
  status: string;
  is_terminal: boolean;
}

export interface UnlinkedHHM {
  user_household_member_id: number;
  first_name: string;
  middle_name: string | null;
  last_name: string;
  household_role: string;
  primary_email: string | null;
  primary_phone: string | null;
  preferred_language: string | null;
}

export interface UseCertificationMembersV2Return {
  members: CertHouseholdMemberData[];
  activeSiblingCerts: ActiveCertification[];
  householdMembersNotOnCert: UnlinkedHHM[];
  isLoading: boolean;
  refetch: () => void;
}

/** Map a CertMember to the CertHouseholdMemberData shape. */
function mapCertMember(m: CertMember): CertHouseholdMemberData {
  return {
    cert_member_id: m.cert_member_id,
    user_household_member_id: m.user_household_member_id,
    household_role: m.is_head_of_household
      ? 'hoh'
      : m.is_helper
      ? 'helper'
      : 'member',
    first_name: m.first_name ?? '',
    middle_name: m.middle_name ?? '',
    last_name: m.last_name ?? '',
    preferred_language: '',
    member_type_id: m.member_type_id,
    member_type_name: m.member_type_name,
    other_cert_memberships: [],
  };
}

export function useCertificationMembersV2(
  certId: number
): UseCertificationMembersV2Return {
  const certifications = useSelector(
    (state: RootState) => state.newRenterInterface.certifications
  ) as Certification[];

  // Derive members from Redux cert data (same as before)
  const cert = useMemo(
    () => (certifications ?? []).find((c) => c.id === certId),
    [certifications, certId]
  );

  const members = useMemo<CertHouseholdMemberData[]>(() => {
    const raw = cert?.cert_household?.cert_members ?? [];
    return raw.map(mapCertMember);
  }, [cert]);

  // Live fetch of household_members_not_on_cert
  const [householdMembersNotOnCert, setHouseholdMembersNotOnCert] = useState<
    UnlinkedHHM[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchNotOnCert = useCallback(async () => {
    if (!certId) {
      return;
    }
    setIsLoading(true);
    try {
      const { data } = await axios.get(
        `certification/${certId}/household/members/`
      );
      setHouseholdMembersNotOnCert(
        (data.household_members_not_on_cert ?? []).map(
          (m: {
            user_household_member_id: number;
            first_name: string | null;
            middle_name: string | null;
            last_name: string | null;
            household_role: string;
            primary_email: string | null;
            primary_phone: string | null;
            preferred_language: string | null;
          }) => ({
            user_household_member_id: m.user_household_member_id,
            first_name: m.first_name ?? '',
            middle_name: m.middle_name ?? null,
            last_name: m.last_name ?? '',
            household_role: m.household_role,
            primary_email: m.primary_email ?? null,
            primary_phone: m.primary_phone ?? null,
            preferred_language: m.preferred_language ?? null,
          })
        )
      );
    } catch {
      setHouseholdMembersNotOnCert([]);
    } finally {
      setIsLoading(false);
    }
  }, [certId]);

  useEffect(() => {
    fetchNotOnCert();
  }, [fetchNotOnCert]);

  return {
    members,
    activeSiblingCerts: [],
    householdMembersNotOnCert,
    isLoading,
    refetch: fetchNotOnCert,
  };
}

// Re-export v2 type so callers can import from one place.
export type { CertificationMemberView };
